/**
 * useDashboardData — 仪表盘 widget 数据 Hook
 *
 * 从 GET /api/v1/dashboard/widget-data 获取 KPI 聚合数据
 * 支持: count, status-distribution, recent, type-distribution
 */

import { useEffect, useState, useCallback } from 'react';
import { api } from '@/lib/api';

// ─── Types ─────────────────────────────────────────

export interface WidgetCount {
  total: number;
}

export interface StatusGroup {
  status: string;
  count: number;
}

export interface TypeGroup {
  type: string;
  count: number;
}

export interface WidgetStatusDistribution {
  groups: StatusGroup[];
}

export interface WidgetTypeDistribution {
  groups: TypeGroup[];
}

export interface WidgetRecent<T = Record<string, unknown>> {
  items: T[];
}

type WidgetType = 'count' | 'status-distribution' | 'recent' | 'type-distribution';

interface WidgetQuery {
  slug: string;
  type: WidgetType;
  limit?: number;
  groupBy?: string;
  filters?: Record<string, string>;
}

interface DashboardDataResult<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

// ─── Single Widget Hook ────────────────────────────

export function useWidgetData<T>(query: WidgetQuery): DashboardDataResult<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        slug: query.slug,
        type: query.type,
      });
      if (query.limit) params.set('limit', String(query.limit));
      if (query.groupBy) params.set('groupBy', query.groupBy);
      if (query.filters) {
        for (const [k, v] of Object.entries(query.filters)) {
          params.set(`filters[${k}]`, v);
        }
      }
      const result = await api.get<T>(`/dashboard/widget-data?${params.toString()}`);
      setData(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load widget data');
    } finally {
      setLoading(false);
    }
  }, [query.slug, query.type, query.limit, query.groupBy, JSON.stringify(query.filters)]);

  useEffect(() => {
    void fetchData();
  }, [fetchData]);

  return { data, loading, error, refresh: fetchData };
}

// ─── Batch Widget Dashboard Hook ───────────────────

interface DashboardConfig {
  counts?: Array<{ key: string; slug: string; filters?: Record<string, string> }>;
  statusDistributions?: Array<{ key: string; slug: string }>;
  recentLists?: Array<{ key: string; slug: string; limit?: number }>;
}

interface DashboardState {
  counts: Record<string, number>;
  distributions: Record<string, StatusGroup[]>;
  recentItems: Record<string, Record<string, unknown>[]>;
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

export function useDashboardData(config: DashboardConfig): DashboardState {
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [distributions, setDistributions] = useState<Record<string, StatusGroup[]>>({});
  const [recentItems, setRecentItems] = useState<Record<string, Record<string, unknown>[]>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const widgets = [
        ...(config.counts ?? []).map((c) => ({
          key: c.key,
          slug: c.slug,
          type: 'count' as const,
          filters: c.filters,
        })),
        ...(config.statusDistributions ?? []).map((d) => ({
          key: d.key,
          slug: d.slug,
          type: 'status-distribution' as const,
        })),
        ...(config.recentLists ?? []).map((r) => ({
          key: r.key,
          slug: r.slug,
          type: 'recent' as const,
          limit: r.limit ?? 10,
        })),
      ];

      if (widgets.length === 0) {
        setCounts({});
        setDistributions({});
        setRecentItems({});
        return;
      }

      const result = await api.post<{
        widgets: Record<string, WidgetCount | WidgetStatusDistribution | WidgetRecent | { error?: string }>;
      }>('/dashboard/widgets/batch', { widgets });

      const nextCounts: Record<string, number> = {};
      const nextDistributions: Record<string, StatusGroup[]> = {};
      const nextRecentItems: Record<string, Record<string, unknown>[]> = {};

      for (const c of config.counts ?? []) {
        const item = result.widgets[c.key] as WidgetCount | undefined;
        nextCounts[c.key] = typeof item?.total === 'number' ? item.total : 0;
      }
      for (const d of config.statusDistributions ?? []) {
        const item = result.widgets[d.key] as WidgetStatusDistribution | undefined;
        nextDistributions[d.key] = Array.isArray(item?.groups) ? item.groups : [];
      }
      for (const r of config.recentLists ?? []) {
        const item = result.widgets[r.key] as WidgetRecent | undefined;
        nextRecentItems[r.key] = Array.isArray(item?.items) ? item.items : [];
      }

      setCounts(nextCounts);
      setDistributions(nextDistributions);
      setRecentItems(nextRecentItems);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Dashboard load failed');
    } finally {
      setLoading(false);
    }
  }, [JSON.stringify(config)]);

  useEffect(() => {
    void fetchAll();
  }, [fetchAll]);

  return { counts, distributions, recentItems, loading, error, refresh: fetchAll };
}
